import type { AiState } from './types'
import {
  REQUIRED_MSG,
  TENANT_MULTI_MSG,
  dropStyle,
  functionCallingOptions,
  TENANT_SCOPE_OPTIONS,
} from './constants'
import { FieldCell, TwoColRow } from './styles'
import { filterTenantDropdownOption } from './hydration'
import WizrDropDown from '../../WizrDropDown'

interface AiTenantSectionProps {
  aiState: Pick<AiState, 'functionCallingEnabled' | 'tenantScope' | 'productId'>
  setAiField: (key: keyof AiState, value: string) => void
  aiErrors: Partial<Record<string, boolean>>
  clearAiErrorKey: (key: string) => void
  isMasterTenant: boolean
  existingRecordId: string | undefined
  tenantScopeSelected: boolean
  setTenantScopeSelected: (v: boolean) => void
  selectedTenantIds: string[]
  setSelectedTenantIds: React.Dispatch<React.SetStateAction<string[]>>
  tenantListPending: boolean
  tenantListOptions: { label: string; value: string }[]
  productOptions: { label: string; value: string }[]
  productListPending: boolean
}

const LlmDbTenantSection = ({
  aiState,
  setAiField,
  aiErrors,
  clearAiErrorKey,
  isMasterTenant,
  existingRecordId,
  tenantScopeSelected,
  setTenantScopeSelected,
  selectedTenantIds,
  setSelectedTenantIds,
  tenantListPending,
  tenantListOptions,
  productOptions,
  productListPending,
}: AiTenantSectionProps): JSX.Element => {
  const productFieldCell = (
    <FieldCell label='Select Product' fieldKey='productId'>
      <WizrDropDown
        placeHolder={productListPending ? 'Loading products...' : 'Select Product'}
        loading={productListPending}
        options={productOptions}
        value={aiState.productId || undefined}
        onChange={(v: string) => {
          setAiField('productId', v)
          if (v) clearAiErrorKey('productId')
        }}
        style={dropStyle}
        showError={!!aiErrors.productId}
        errorMessage={aiErrors.productId ? REQUIRED_MSG : undefined}
      />
    </FieldCell>
  )

  return (
    <>
      <TwoColRow
        left={
          <FieldCell label='Function Calling Enabled' fieldKey='functionCallingEnabled'>
            <WizrDropDown
              placeHolder='Select'
              options={functionCallingOptions}
              value={aiState.functionCallingEnabled || undefined}
              onChange={(v: string) => {
                setAiField('functionCallingEnabled', v)
                if (v) clearAiErrorKey('functionCallingEnabled')
              }}
              style={dropStyle}
              showError={!!aiErrors.functionCallingEnabled}
              errorMessage={aiErrors.functionCallingEnabled ? REQUIRED_MSG : undefined}
            />
          </FieldCell>
        }
        right={
          <FieldCell label='Tenant' fieldKey='tenantScope'>
            <WizrDropDown
              placeHolder={isMasterTenant ? 'Select tenant scope' : undefined}
              options={
                isMasterTenant
                  ? TENANT_SCOPE_OPTIONS
                  : [{ label: 'Tenant Specific', value: 'specific' }]
              }
              value={isMasterTenant ? aiState.tenantScope || undefined : 'specific'}
              onChange={(v: string) => {
                setAiField('tenantScope', v as any)
                setTenantScopeSelected(true)
                if (v === 'basic') setSelectedTenantIds([])
                clearAiErrorKey('tenantScope')
                clearAiErrorKey('tenantMulti')
              }}
              style={dropStyle}
              disabled={!isMasterTenant}
              showError={!!aiErrors.tenantScope}
              errorMessage={aiErrors.tenantScope ? REQUIRED_MSG : undefined}
            />
          </FieldCell>
        }
      />

      {isMasterTenant && aiState.tenantScope === 'specific' ? (
        <TwoColRow
          left={
            <FieldCell label='Tenants' fieldKey='tenantMulti'>
              <WizrDropDown
                mode={existingRecordId ? undefined : 'multiple'}
                placeHolder={
                  tenantListPending
                    ? 'Loading tenants...'
                    : existingRecordId
                      ? 'Select tenant'
                      : 'Select one or more tenants'
                }
                loading={tenantListPending}
                options={tenantListOptions}
                value={
                  existingRecordId
                    ? selectedTenantIds[0] != null && selectedTenantIds[0] !== ''
                      ? selectedTenantIds[0]
                      : undefined
                    : selectedTenantIds
                }
                onChange={(v: string | string[]) => {
                  if (existingRecordId) {
                    const id =
                      v == null || v === ''
                        ? ''
                        : Array.isArray(v)
                          ? String(v[v.length - 1] ?? '')
                          : String(v)
                    const trimmed = id.trim()
                    setSelectedTenantIds(trimmed ? [trimmed] : [])
                    if (trimmed) clearAiErrorKey('tenantMulti')
                  } else {
                    const arr = Array.isArray(v)
                      ? v.map((x) => String(x).trim()).filter(Boolean)
                      : []
                    setSelectedTenantIds(arr)
                    if (arr.length) clearAiErrorKey('tenantMulti')
                  }
                }}
                style={dropStyle}
                showSearch={!existingRecordId}
                filterOption={filterTenantDropdownOption}
                maxTagCount='responsive'
                showError={!!aiErrors.tenantMulti}
                errorMessage={aiErrors.tenantMulti ? TENANT_MULTI_MSG : undefined}
              />
            </FieldCell>
          }
          right={productFieldCell}
        />
      ) : (
        <TwoColRow left={productFieldCell} />
      )}
    </>
  )
}

export default LlmDbTenantSection
